<script>
import axios from 'axios'
import vSelect from 'vue-select'
import clonedeep from 'lodash.clonedeep'
import uniqby from 'lodash.uniqby'

import { mapGetters } from 'vuex'

import store from '@/store'

import HorizontalBar from '@/components/HorizontalBar.vue'

import * as d3 from "d3"
import format from 'date-fns/format'

import { DistrictDashboardHediColors } from '@/helpers/bgColor'
import { getSchoolTypes } from '@/helpers/getSchoolTypes'

const apiBase = API_APP_PATH + "/v1"

export default {
    data() {
        return {
            selectedYear: null,
            districts: null,
            selectedDistricts: [],
            checkedDistricts: [],
            showAllDistricts: false,
            schools: null,
            schoolYears: null,
            displayedSchools: [],
            selectedSchoolTypes: [],
            checkedTypes: [],
            displaySchoolTypes: false,
            displayDistricts: false,
            filters: {},
            sort: {
                category: null, 
                direction: null
            },
            localValue: "",
            toggle: 'average'
        }
    },
    components: {
        HorizontalBar
    },
    computed: {
        ...mapGetters({
            _districts: 'admin/getDistricts',
            fiscalYear: 'getFiscalYear'
        }),
        // returning false until we circle back to viz design changes
        dontDisplayViz () {
            return false
        },
        areSchoolsSelected () {
            return this.displayedSchoolsByYear.length > 0
        },
        displayedSchoolsByYear () {
            let asc
            let desc
            if (this.sort.category !== 'SchoolDBN_Name') {
                asc = (a, b) => (a[this.sort.category] - b[this.sort.category])
                desc = (a, b) => (b[this.sort.category] - a[this.sort.category])
            } else {
                asc = (a, b) => {
                    if (a[this.sort.category] < b[this.sort.category]) {
                        return -1
                    }
                    if (a[this.sort.category] > b[this.sort.category]) {
                        return 1
                    }
                    return 0
                }
                desc = (a, b) => {
                    if (b[this.sort.category] < a[this.sort.category]) {
                        return -1
                    }
                    if (b[this.sort.category] > a[this.sort.category]) {
                        return 1
                    }
                    return 0
                }
            }
            const schools = this._districts.schools

            let result = this.getSelectedSchools(schools, this.checkedDistricts, this.checkedTypes)

            if (!result.length) return false

            if (this.sort.category) {
                result = result.sort(this.sort.direction === 'desc' ? desc : asc)
            }
            return uniqby(result.filter(x=>x.FiscalYear == this.selectedYear), 'SchoolDBN')
        },
        schoolTypes () {
            // return this.displayedSchools.length ? getSchoolTypes(this.displayedSchools) : getSchoolTypes(this._districts.schools)
            return getSchoolTypes(this._districts.schools).sort((a,b) => {
                if (a < b) {
                return -1
                }
                if (a > b) {
                return 1
                }
                // names must be equal
                return 0
            })
        },
        filteredAverage () {
            const schools = this._districts.schools

            let result = this.getSelectedSchools(schools, this.checkedDistricts, this.checkedTypes)

            const means = this.findMeans(result.filter(x=>x.FiscalYear == this.selectedYear))
  
            if (means.length === 1) {
                return means[0]
            } 
            const combinedMeans = this.combinedMeans(means)
            return this.averagedMeans(combinedMeans)
        },
        overallAverage () {

            const schools = this._districts.schools

            const means = this.findMeans(schools.filter(x=>x.FiscalYear == this.selectedYear))
            
             if (means.length === 1) {
                return means[0]
            } 
            
            const combinedMeans = this.combinedMeans(means)
            
            const result = this.averagedMeans(combinedMeans)

            return result
        },
        filteredAveragesOverTime () {

            let averages = []

            const categories = [
                ['component1e_mean','component1a_mean'],
                ['component2d_mean','component2a_mean'],
                ['component3d_mean','component3c_mean','component3b_mean'],
                ['component4e_mean']
            ]
            
            for (let i=0;i<categories.length;i++) {
                let obj = {}
                for (let j=0; j<categories[i].length;j++) {
                    obj[categories[i][j]] = []
                }
                averages.push(obj)
            }

            const _schools = this.displayedSchools.length > 0 ? this.displayedSchools :this._districts.schools

            const schools = this.getSelectedSchools(_schools, this.selectedDistricts, this.checkedTypes)

            let years = Array.from(new Set(schools.map(x=>x.FiscalYear)))

            years = years.sort((a,b) => b-a)

            const meansMap = new Map()

            for (const value of years) {

                const means = this.findMeans(schools.filter(x=>x.FiscalYear === value))
                
                const selectedMeans = means.length > 1 ? this.combinedMeans(means) : means[0]

                meansMap.set(value, this.averagedMeans(selectedMeans))
            }

            for (let i=0; i <= 2; i++) {
                
                const year = this.fiscalYear - i

                averages = averages.map(domain=> {
                    for (let p in domain) {

                        domain[p].push(
                            {
                                label: year, 
                                value: meansMap.size > 0 ? meansMap.get(year)[p] : ''
                            }
                        )
                    }
                    return domain
                })
            }
            averages = averages.map(avg => {
                for (const item in avg) {
                    avg[item].sort((a,b) => a.label - b.label)
                }
                return avg
            })
            return averages
        },
        allDistrictsSelected () {
            if (this.districts && this.checkedDistricts) {
                return this.checkedDistricts.length === this.districts.length
            }
            return false           
        },
        renderedDistricts () {
            let extra = []
            let districts = clonedeep(this.checkedDistricts)
            districts.length > 14 ? extra = districts.splice(14) : districts
            return {
                extra,
                districts
            }
        },
        displayedDistricts () {
            return this.showAllDistricts ? this.checkedDistricts : this.renderedDistricts.districts
        }
    },
    created () {
        store.commit('currentPage', 'district')
    },
    async mounted () {

        if (!this._districts.selected.length) {
            await store.commit('admin/updateSelectedDistricts', this._districts.list)
        }

        this.selectedYear = this.fiscalYear

        this.schools = new Map()

        this.setDistrictsList(this._districts.list)
        this.setSelectedDistricts(this._districts.selected)

        this.fetchSchools(this._districts.schools)

        this.displayedSchools = this.findSchools(this._districts.selected)

        const years = this.findSchools(this._districts.list).map(x => x.FiscalYear)
        
        this.schoolYears = Array.from(new Set(years)).sort((a, b) => b - a)

    },
    destroyed () {
        store.commit('admin/updateSelectedDistricts', [])
    },
    watch: {
        checkedDistricts: function(districts) {
            this.filters['districts'] = districts
            this.displayedSchools = districts.length > 0 ? this.findSchools(districts) : this.findSchools(this._districts.list)
        },
        checkedTypes: function(types) {
            this.filters['schoolTypes'] = types
        },
        selectedYear: function() {
            this.clearFilters()
        }
    },
    methods: {
        fetchSchools(list) {
            const _schools = this.schools
            for (let i=0; i < this.districts.length; i++) {
                if (!_schools.has(this.districts[i])) {
                    const schools = list.filter(x=> x.District == this.districts[i])
                    this.schools.set(this.districts[i], schools)
                }
            }
        },
        getSelectedSchools (schools, districts, types) {
            let result = []
            let union1, union2, intersection

            if (types.length > 0 || districts.length > 0) {


                if (types.length > 0) {

                    let sets = []
                    for (let i=0; i < types.length; i++) {
                        let set = new Set(schools.filter(x=>x.school_type === types[i]))
                        sets.push(...set)
                    }
                    union1 = new Set(sets)

                    intersection = [...union1]

                }

                if (districts.length > 0) {

                    let sets = []
                    for (let i=0; i < districts.length; i++) {
                        let set = new Set(schools.filter(x=>x.District === districts[i]))
                        sets.push(...set)
                    }
                    union2 = new Set(sets)
    
                    intersection = !union1 ? [...union2] : new Set([...union1].filter(x => union2.has(x)))

                }
                return [...intersection]

            } else {
                return schools
            }
        },
        setSelectedDistricts(districts) {
            /* refactor to avoid use of filter fn below */
            this.checkedDistricts = districts
                .filter(x => x)
                .map(x => {
                    const val = typeof x === 'string' ? x : x.District
                    return val.match(/\d+/g)[0]
                })
        },
        setDistrictsList(list) {
            this.districts = list.map(x=>x.District).sort((a,b) => {
                return parseInt(a) - parseInt(b)
            })
        },
        removeDistrict(district) {
            this.checkedDistricts = this.removeAtIndex(district, this.checkedDistricts)
        },
        selectSchoolType(item) {
            this.selectedSchoolTypes.push(item.target.value)
        },
        removeSchoolType(type) {
            this.checkedTypes = this.removeAtIndex(type, this.checkedTypes)
        },
        removeAtIndex(val, list) {
            const index = list.indexOf(val)
            if (index > -1) {
                list.splice(index, 1)
            }
            return list
        },
        findSchools(districts) {
            let result = []
            for (let i=0; i < districts.length; i++) {
                const district = typeof districts[i] === 'object' ? districts[i].District : districts[i].split(' ')[1]
                result = result.concat(this.schools.get(district))
            }
            return result
        },
        calculateAverageOfComponents(data) {

            const keys = Object.keys(data).filter(x=>/mean/.test(x))

            let values = []

            for (var i=0; i < keys.length; i++) {
                values.push(data[keys[i]])
            }
            return (values.reduce( (a,b) => (a+b)) / values.length).toFixed(2)
        },
        findMeans(data) {
            const means = []
            /* each school  */
            for (const value of data) {
                const o = {}
                /* column in school object */
                for (const i in value) {
                    if (/mean/.test(i)) {
                        o[i] = value[i]
                    } 
                }
                means.push(o)
            }
            return means
        },
        combinedMeans(data) {

            if (data.length === 1) return data[0]
 
            return data.reduce((a,b) => {
                for (let value in b) {
                    Object.keys(a).indexOf(value) === -1 ? a[value] = [] : a[value].push(b[value])
                }
                return a
            }, {})
        },
        averagedMeans(data) {
            const result = {}
            for (let value in data) {
                if (data[value].every(x=> x === null)) {
                    result[value] = null
                } else {
                    const filtered = data[value].filter(v=>v)
                    result[value] = (filtered.reduce((a,b) => a+b, 0) / filtered.length)
                }
            }
            return result
        },
        renderHediColor(avg) {
            return DistrictDashboardHediColors(avg)
        },
        formattedDate() {
            return format(new Date(), 'MMM. DD')
        },
        convertToPercentage(value) {
            return `${(parseFloat(value) * 100).toFixed()}%`
        },
        updateSort(cat, el) {

            let pathElements = this.$refs.components.querySelectorAll('path')

            const paths = el.target.querySelector('svg').children
   
            !this.sort.category ? this.setInitialCategorty(cat) 
            : this.sameCategory(cat) ? this.switchDirection(this.sort.direction) 
            : this.updateCategory(cat)

            const { direction, category } = this.sort

            Array.from(pathElements).forEach(el => {
                el.classList.remove('active')
            })

            if (direction === 'asc') {
                paths[0].classList.toggle('active')
            } else {
                paths[1].classList.toggle('active')
            }
        },
        setInitialCategorty(category) {
            this.sort.category = category
            this.sort.direction = 'desc'
        },
        updateCategory(category) {
            this.sort.category = category
            this.sort.direction = 'desc'
        },
        sameCategory(category) {
            return this.sort.category === category
        },
        switchDirection(direction) {
           this.sort.direction = (direction === 'desc' ? 'asc' : 'desc')
        },
        clearFilters() {
            if (this.districts.length !== 1) {
                this.checkedDistricts = this._districts.list.map(x => x.District)
            }
            this.checkedTypes = []
        },
        hideDistricts() {
            this.displayDistricts = false
        },
        hideSchoolTypes() {
            this.displaySchoolTypes = false
        },       
        concatenateText(text) {
            return text.length > 23 ? `${text.slice(0, 20)}...` : text
        },
        concatenateSchName(text) {
            return text.length > 32 ? `${text.slice(0, 32)}...` : text
        },
        setAllDistricts () {
            this.checkedDistricts = this.allDistrictsSelected ? [] : this.districts
        },
        async goToDashboard(dbn) {
            await store.dispatch('admin/fetchSelectedSchool', {dbn: dbn})
            this.$router.push({path: 'school-dashboard'})
        }
    }
}
</script>
<template>
    <div class="container">
        <div class="header">
            <h2>District Dashboard</h2>
            <p v-if="checkedDistricts.length > 0 && !allDistrictsSelected" class="subtitle">Measures of Teacher Practice Average by Component: {{ checkedDistricts.length > 1 ? `Districts` : `District` }} {{ checkedDistricts.join(',') }}</p>
            <p v-else-if="allDistrictsSelected" class="subtitle">Measures of Teacher Practice Average by Component: All Districts</p>      
            <p v-else class="subtitle">Measures of Teacher Practice Average by Component: No Schools</p>
        </div>
        <div class="dashboard">
            <p class="description">Use this table to view Measures of Teacher Practice for all of the schools you support. Use the "Frequency Rated" function to understand <br> how often evaluators are rating components during observations.</p>
            <div class="filter" v-if="districts">
                <div>
                    <div class="dash-filters">
                        <div class="toggle">
                            <label>View By:</label>
                            <div>
                                <button v-on:click="toggle = 'average'" type="button" v-bind:class="{active: toggle === 'average'}" class="average">Average</button>
                                <button v-on:click="toggle = 'frequency'" type="button" v-bind:class="{active: toggle === 'frequency'}" class="count">Frequency</button>
                            </div>
                        </div>
                        <div class="filter-group">
                            <div class="filter">
                                <label for="year">Year</label>
                                <select v-model="selectedYear" name="year" id="year">
                                    <option v-for="year in schoolYears" :key="year" :value="year">{{year-1}} - {{year}}</option>
                                </select>
                            </div>
                            <div class="filter">
                                <div v-if="_districts.list.length>1">
                                    <label for="district">District</label>
                                    <button 
                                        v-on:click="displayDistricts = !displayDistricts"
                                        class="dropdown-btn"
                                    >
                                        Select a District(s)
                                        <svg class="down-chevron">
                                            <line x1="0" y1="3" x2="6" y2="8" stroke="black" stroke-width="2" />
                                            <line x1="6" y1="8" x2="12" y2="3" stroke="black" stroke-width="2" />
                                        </svg>
                                    </button>
                                    <transition name="fade">
                                        <div 
                                            v-if="displayDistricts"
                                            class="dropdown"
                                        >
                                            <ul @mouseleave="hideDistricts">
                                                <li>
                                                    <input type="checkbox" id="all-districts" :checked="allDistrictsSelected" @change="setAllDistricts">
                                                    <label for="all district">All Districts</label>
                                                </li>
                                                <li 
                                                    v-for="district in districts"
                                                    :key="district"
                                                >
                                                    <input type="checkbox" :id="district" :value="district" v-model="checkedDistricts">
                                                    <label :for="district"> District {{ district }}</label>
                                                </li>
                                            </ul>
                                        </div>
                                    </transition>
                                </div>
                                <div v-else class="district">
                                    <p>District</p>
                                    <p class="value">{{_districts.list[0].District}}</p>
                                </div>
                            </div>
                            <div class="filter">
                                <label for="school-type">School Type</label>
                                <button 
                                    v-on:click="displaySchoolTypes = !displaySchoolTypes"
                                    class="dropdown-btn"
                                >
                                    Select a School Type(s)
                                    <svg class="down-chevron">
                                        <line x1="0" y1="3" x2="6" y2="8" stroke="black" stroke-width="2" />
                                        <line x1="6" y1="8" x2="12" y2="3" stroke="black" stroke-width="2" />
                                    </svg>
                                </button>
                                <transition name="fade">
                                    <div 
                                        v-if="displaySchoolTypes"
                                        class="dropdown"
                                    >
                                        
                                        <ul @mouseleave="hideSchoolTypes">
                                            <li 
                                                v-for="type in schoolTypes"
                                                :key="type"
                                            >
                                                <input type="checkbox" :id="type" :value="type" v-model="checkedTypes">
                                                <label :for="type">{{ type }}</label>
                                            </li>
                                        </ul>
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </div> <!-- end of filters -->
                    <div>
                        <div 
                            v-if="checkedTypes.length > 0 || ( (checkedDistricts.length > 0 && !allDistrictsSelected) && districts.length > 1)"
                            class="applied-filters"
                        >
                            <h3>Filtering by:</h3>
                            <div
                                v-if="districts.length > 1 && !allDistrictsSelected"
                                class="selected-districts"
                            >
                                <ul>
                                    <li
                                        v-for="district in displayedDistricts"
                                        :key="district" 
                                    >
                                        <svg
                                            v-on:click="removeDistrict(district)" 
                                            width="10" 
                                            height="10"
                                        >
                                            <line x1="0" y1="0" x2="10" y2="10" stroke-width="2px" stroke="black" />
                                            <line x1="0" y1="10" x2="10" y2="0" stroke-width="2px" stroke="black" />
                                        </svg>
                                        {{ `${district}`}}
                                    </li>
                                    <button
                                        class="see-more-btn"
                                        v-if="!showAllDistricts && renderedDistricts.extra.length > 0"
                                        v-on:click="showAllDistricts = !showAllDistricts"
                                    >
                                        + See {{ renderedDistricts.extra.length }} More
                                    </button>
                                    <button 
                                        v-if="checkedTypes.length === 0"
                                        v-on:click="clearFilters()"
                                        class="clear-filters-btn"
                                    >
                                        <svg width="10" height="10">
                                            <line x1="0" y1="0" x2="10" y2="10" stroke-width="2px" stroke="black" />
                                            <line x1="0" y1="10" x2="10" y2="0" stroke-width="2px" stroke="black" />
                                        </svg>
                                        Clear Filters
                                    </button>
                                </ul>
                            </div>
                            <div class="selected-types">
                                <ul>
                                    <li 
                                        v-for="type in checkedTypes"
                                        :key="type"
                                    >
                                        <svg
                                            v-on:click="removeSchoolType(type)" 
                                            width="10" 
                                            height="10"
                                        >
                                            <line x1="0" y1="0" x2="10" y2="10" stroke-width="2px" stroke="black" />
                                            <line x1="0" y1="10" x2="10" y2="0" stroke-width="2px" stroke="black" />
                                        </svg>
                                        {{ concatenateText(type) }}
                                    </li>
                                    <button 
                                        v-if="checkedTypes.length > 0"
                                        v-on:click="clearFilters()"
                                        class="clear-filters-btn"
                                    >
                                        <svg width="10" height="10">
                                            <line x1="0" y1="0" x2="10" y2="10" stroke-width="2px" stroke="black" />
                                            <line x1="0" y1="10" x2="10" y2="0" stroke-width="2px" stroke="black" />
                                        </svg>
                                        Clear Filters
                                    </button>
                                </ul>
                            </div>
                        </div>
                        <div 
                            v-if="checkedDistricts.length < 1"
                            class="no-districts"
                        >
                            <p>Please select a district to see school level component averages displayed.</p>
                        </div>
                        <div v-else-if="areSchoolsSelected">
                            <div class="hedi-scale">
                                <img src="@/assets/images/HEDIScale.png" />
                            </div>
                            <table
                                
                            >
                                <thead>
                                    <tr ref="components" class="components">
                                        <th v-on:click="updateSort('SchoolDBN_Name', $event)">
                                            <span>School Name, <br> School DBN</span>
                                            <div>
                                                <svg width="12px" height="16px">
                                                    <path d="M 6 0 L 0 6 L 12 6 Z" fill="transparent" stroke="#949494" />
                                                    <path d="M 6 16 L 0 10 L 12 10 Z" fill="transparent" stroke="#949494" />
                                                </svg>
                                            </div>
                                        </th>
                                        <th v-on:click="updateSort('component1a_mean', $event)" scope="col" class="col">
                                            <span>1A</span> 
                                            <div>
                                                <svg width="12px" height="16px">
                                                    <path d="M 6 0 L 0 6 L 12 6 Z" fill="transparent" stroke="#949494" />
                                                    <path d="M 6 16 L 0 10 L 12 10 Z" fill="transparent" stroke="#949494" />
                                                </svg>
                                            </div>
                                        </th>
                                        <th v-on:click="updateSort('component1e_mean', $event)" scope="col" class="col">
                                            <span>1E</span> 
                                            <div>
                                                <svg width="12px" height="16px">
                                                    <path d="M 6 0 L 0 6 L 12 6 Z" fill="transparent" stroke="#949494" />
                                                    <path d="M 6 16 L 0 10 L 12 10 Z" fill="transparent" stroke="#949494" />
                                                </svg>
                                            </div>
                                        </th>
                                        <th v-on:click="updateSort('component2a_mean', $event)" scope="col" class="col">
                                            <span>2E</span> 
                                            <div>
                                                <svg width="12px" height="16px">
                                                    <path d="M 6 0 L 0 6 L 12 6 Z" fill="transparent" stroke="#949494" />
                                                    <path d="M 6 16 L 0 10 L 12 10 Z" fill="transparent" stroke="#949494" />
                                                </svg>
                                            </div>
                                        </th>
                                        <th v-on:click="updateSort('component2d_mean', $event)" scope="col" class="col">
                                            <span>2D</span> 
                                            <div>
                                                <svg width="12px" height="16px">
                                                    <path d="M 6 0 L 0 6 L 12 6 Z" fill="transparent" stroke="#949494" />
                                                    <path d="M 6 16 L 0 10 L 12 10 Z" fill="transparent" stroke="#949494" />
                                                </svg>
                                            </div>
                                        </th>
                                        <th v-on:click="updateSort('component3b_mean', $event)" scope="col" class="col">
                                            <span>3B</span> 
                                            <div>
                                                <svg width="12px" height="16px">
                                                    <path d="M 6 0 L 0 6 L 12 6 Z" fill="transparent" stroke="#949494" />
                                                    <path d="M 6 16 L 0 10 L 12 10 Z" fill="transparent" stroke="#949494" />
                                                </svg>
                                            </div>
                                        </th>
                                        <th v-on:click="updateSort('component3c_mean', $event)" scope="col" class="col">
                                            <span>3C</span> 
                                            <div>
                                                <svg width="12px" height="16px">
                                                    <path d="M 6 0 L 0 6 L 12 6 Z" fill="transparent" stroke="#949494" />
                                                    <path d="M 6 16 L 0 10 L 12 10 Z" fill="transparent" stroke="#949494" />
                                                </svg>
                                            </div>
                                        </th>
                                        <th v-on:click="updateSort('component3d_mean', $event)" scope="col" class="col">
                                            <span>3D</span> 
                                            <div>
                                                <svg width="12px" height="16px">
                                                    <path d="M 6 0 L 0 6 L 12 6 Z" fill="transparent" stroke="#949494" />
                                                    <path d="M 6 16 L 0 10 L 12 10 Z" fill="transparent" stroke="#949494" />
                                                </svg>
                                            </div>
                                        </th>
                                        <th v-on:click="updateSort('component4e_mean', $event)" scope="col" class="col">
                                            <span>4E</span> 
                                            <div>
                                                <svg width="12px" height="16px">
                                                    <path d="M 6 0 L 0 6 L 12 6 Z" fill="transparent" stroke="#949494" />
                                                    <path d="M 6 16 L 0 10 L 12 10 Z" fill="transparent" stroke="#949494" />
                                                </svg>
                                            </div>
                                        </th>
                                        <!-- <th v-on:click="updateSort('SL_MOTP_Score_mean', $event)" colspan="1" rowspan="2">Score to Date</th> -->
                                        <th v-on:click="updateSort('SL_MOTP_Score_mean', $event)">
                                            <span v-if="selectedYear === 2020">*</span> Score to Date
                                            <div>
                                                <svg width="12px" height="16px">
                                                    <path d="M 6 0 L 0 6 L 12 6 Z" fill="transparent" stroke="#949494" />
                                                    <path d="M 6 16 L 0 10 L 12 10 Z" fill="transparent" stroke="#949494" />
                                                </svg>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(sch, i) in displayedSchoolsByYear" :key="i">
                                        <td @click="goToDashboard(sch.SchoolDBN)">{{ concatenateSchName(sch.SchoolDBN_Name) }}, <br> {{sch.SchoolDBN}}</td>
                                        <td class="component-data">
                                            <span v-if="toggle == 'average'" :class="{'no-data': !sch.component1a_mean}" v-bind:style="{backgroundColor: sch.component1a_mean ? renderHediColor(sch.component1a_mean) : ''}">{{sch.component1a_mean ? sch.component1a_mean.toFixed(2) : 'N/A' }}</span>
                                            <span v-else v-bind:class="[{ nofreq: sch.component1a_freq === 0 }, 'freq']">{{convertToPercentage(sch.component1a_freq)}}</span>
                                        </td>
                                        <td class="component-data">
                                            <span v-if="toggle == 'average'" :class="{'no-data': !sch.component1e_mean}" v-bind:style="{backgroundColor: sch.component1e_mean ? renderHediColor(sch.component1e_mean) : ''}">{{sch.component1e_mean ? sch.component1e_mean.toFixed(2) : 'N/A'}}</span>
                                            <span v-else v-bind:class="[{ nofreq: sch.component1e_freq === 0 }, 'freq']">{{convertToPercentage(sch.component1e_freq)}}</span>
                                        </td>
                                        <td class="component-data">
                                            <span v-if="toggle == 'average'" :class="{'no-data': !sch.component2a_mean}" v-bind:style="{backgroundColor: sch.component2a_mean ? renderHediColor(sch.component2a_mean) : ''}">{{sch.component2a_mean ? sch.component2a_mean.toFixed(2) : 'N/A'}}</span>
                                            <span v-else v-bind:class="[{ nofreq: sch.component2a_freq === 0 }, 'freq']">{{convertToPercentage(sch.component2a_freq)}}</span>
                                        </td>
                                        <td class="component-data">
                                            <span v-if="toggle == 'average'" :class="{'no-data': !sch.component2d_mean}" v-bind:style="{backgroundColor: sch.component2d_mean ? renderHediColor(sch.component2d_mean) : ''}">{{sch.component2d_mean ? sch.component2d_mean.toFixed(2) : 'N/A'}}</span>
                                            <span v-else v-bind:class="[{ nofreq: sch.component2d_freq === 0 }, 'freq']">{{convertToPercentage(sch.component2d_freq)}}</span>
                                        </td>
                                        <td class="component-data">
                                            <span v-if="toggle == 'average'" :class="{'no-data': !sch.component3b_mean}" v-bind:style="{backgroundColor: sch.component3b_mean ? renderHediColor(sch.component3b_mean) : ''}">{{sch.component3b_mean ? sch.component3b_mean.toFixed(2) : 'N/A'}}</span>
                                            <span v-else v-bind:class="[{ nofreq: sch.component3b_freq === 0 }, 'freq']">{{convertToPercentage(sch.component3b_freq)}}</span>
                                        </td>
                                        <td class="component-data">
                                            <span v-if="toggle == 'average'" :class="{'no-data': !sch.component3c_mean}" v-bind:style="{backgroundColor: sch.component3c_mean ? renderHediColor(sch.component3c_mean) : ''}">{{sch.component3c_mean ? sch.component3c_mean.toFixed(2) : 'N/A'}}</span>
                                            <span v-else v-bind:class="[{ nofreq: sch.component3c_freq === 0 }, 'freq']">{{convertToPercentage(sch.component3c_freq)}}</span>
                                        </td>
                                        <td class="component-data">
                                            <span v-if="toggle == 'average'" :class="{'no-data': !sch.component3d_mean}" v-bind:style="{backgroundColor: sch.component3d_mean ? renderHediColor(sch.component3d_mean) : ''}">{{sch.component3d_mean ? sch.component3d_mean.toFixed(2) : 'N/A'}}</span>
                                            <span v-else v-bind:class="[{ nofreq: sch.component3d_freq === 0 }, 'freq']">{{convertToPercentage(sch.component3d_freq)}}</span>
                                        </td>
                                        <td class="component-data">
                                            <span v-if="toggle == 'average'" :class="{'no-data': !sch.component4e_mean}" v-bind:style="{backgroundColor: sch.component4e_mean ? renderHediColor(sch.component4e_mean) : ''}">{{sch.component4e_mean ? sch.component4e_mean.toFixed(2) : 'N/A'}}</span>
                                            <span v-else v-bind:class="[{ nofreq: sch.component4e_freq === 0 }, 'freq']">{{convertToPercentage(sch.component4e_freq)}}</span>
                                        </td>
                                        <td><span>{{sch.SL_MOTP_Score_mean !== null ? sch.SL_MOTP_Score_mean.toFixed(2) : selectedYear === 2020 ? '' : 'N/A' }}</span></td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr 
                                        v-if="!allDistrictsSelected || this.checkedTypes.length > 0"
                                    >
                                        <td>Filtered Average</td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !filteredAverage.component1a_mean}" v-bind:style="{backgroundColor: filteredAverage.component1a_mean ? renderHediColor(filteredAverage.component1a_mean) : ''}">
                                                {{ filteredAverage.component1a_mean ? filteredAverage.component1a_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !filteredAverage.component1e_mean}" v-bind:style="{backgroundColor: filteredAverage.component1e_mean ? renderHediColor(filteredAverage.component1e_mean) : ''}">
                                                {{ filteredAverage.component1e_mean ? filteredAverage.component1e_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !filteredAverage.component2a_mean}" v-bind:style="{backgroundColor: filteredAverage.component2a_mean ? renderHediColor(filteredAverage.component2a_mean) : ''}">
                                                {{ filteredAverage.component2a_mean ? filteredAverage.component2a_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !filteredAverage.component2d_mean}" v-bind:style="{backgroundColor: filteredAverage.component2d_mean ? renderHediColor(filteredAverage.component2d_mean) : ''}">
                                                {{ filteredAverage.component2d_mean ? filteredAverage.component2d_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !filteredAverage.component3b_mean}" v-bind:style="{backgroundColor: filteredAverage.component3b_mean ? renderHediColor(filteredAverage.component3b_mean) : ''}">
                                                {{ filteredAverage.component3b_mean ? filteredAverage.component3b_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !filteredAverage.component3c_mean}" v-bind:style="{backgroundColor: filteredAverage.component3c_mean ? renderHediColor(filteredAverage.component3c_mean) : ''}">
                                                {{ filteredAverage.component3c_mean ? filteredAverage.component3c_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !filteredAverage.component3d_mean}" v-bind:style="{backgroundColor: filteredAverage.component3d_mean ? renderHediColor(filteredAverage.component3d_mean) : ''}">
                                                {{ filteredAverage.component3d_mean ? filteredAverage.component3d_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !filteredAverage.component4e_mean}" v-bind:style="{backgroundColor: filteredAverage.component4e_mean ? renderHediColor(filteredAverage.component4e_mean) : ''}">
                                                {{ filteredAverage.component4e_mean ? filteredAverage.component4e_mean.toFixed(2) : 'N/A' }}
                                            </span>    
                                        </td>
                                        <td>
                                            <span />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>All Schools Average</td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !overallAverage.component4e_mean}" v-bind:style="{backgroundColor: overallAverage.component1a_mean ? renderHediColor(overallAverage.component1a_mean) : ''}">
                                                {{ overallAverage.component1a_mean ? overallAverage.component1a_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !overallAverage.component1e_mean}" v-bind:style="{backgroundColor: overallAverage.component1e_mean ? renderHediColor(overallAverage.component1e_mean) : ''}">
                                                {{ overallAverage.component1e_mean ? overallAverage.component1e_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !overallAverage.component2a_mean}" v-bind:style="{backgroundColor: overallAverage.component2a_mean ? renderHediColor(overallAverage.component2a_mean) : ''}">
                                                {{ overallAverage.component2a_mean ? overallAverage.component2a_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !overallAverage.component2d_mean}" v-bind:style="{backgroundColor: overallAverage.component2d_mean ? renderHediColor(overallAverage.component2d_mean) : ''}">
                                                {{ overallAverage.component2d_mean ? overallAverage.component2d_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !overallAverage.component3b_mean}" v-bind:style="{backgroundColor: overallAverage.component3b_mean ? renderHediColor(overallAverage.component3b_mean) : ''}">
                                                {{ overallAverage.component3b_mean ? overallAverage.component3b_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !overallAverage.component3c_mean}" v-bind:style="{backgroundColor: overallAverage.component3c_mean ? renderHediColor(overallAverage.component3c_mean) : ''}">
                                                {{ overallAverage.component3c_mean ? overallAverage.component3c_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !overallAverage.component3d_mean}" v-bind:style="{backgroundColor: overallAverage.component3d_mean ? renderHediColor(overallAverage.component3d_mean) : ''}">
                                                {{ overallAverage.component3d_mean ? overallAverage.component3d_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="component-data">
                                            <span :class="{'no-data': !overallAverage.component4e_mean}" v-bind:style="{backgroundColor: overallAverage.component4e_mean ? renderHediColor(overallAverage.component4e_mean) : ''}">
                                                {{ overallAverage.component4e_mean ? overallAverage.component4e_mean.toFixed(2) : 'N/A' }}
                                            </span>
                                        </td>
                                        <td>
                                            <span />
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                            <div class="table-notes">
                                <p v-if="selectedYear === 2021">* Please note that observations for the 20-21 school year were added beginning in March 2021.</p>
                                <p v-if="selectedYear === 2020">* Please note, teacher evaluations were suspended in mid-March 2020 due to COVID-19 so SY 19-20 MOTP is incomplete.</p>
                            </div>
                        </div>
                        <div v-else> There are no schools for the selected filters</div>
                    </div>
                </div>
            </div>
            <div
                v-if="dontDisplayViz"
                class="district-visualizations"
            >
                <p v-if="checkedDistricts.length > 0">Component Average Over Time: {{ checkedDistricts.length > 1 ? `Districts` : `District` }} {{ checkedDistricts.join(',') }}</p>
                <p v-else>Component Average Over Time: All Schools</p>
                <div class="content">
                    <div>
                        <div>
                            <p>Here you can view averages from the most recent school year as of today's date compared to data from the prior two school years as of the same date in that calendar year.</p>
                        </div>
                    </div>
                    <div class="district-visualizations-legend">
                        <!-- removed years variable need to reconsider -->
                        <ul>
                            <!-- <li v-for="(year, i) in years" :key="i">
                                <span></span> SY {{ (year - 1).toLocaleString().slice(-2) }} - {{ (year).toLocaleString().slice(-2) }}
                            </li> -->
                        </ul>
                    </div>
                    <div id="district-visualizations">
                        <HorizontalBar 
                        v-for="(item, i) in filteredAveragesOverTime" 
                        :key="i" 
                        :title="['Domain 1: Planning & Preparation', 'Domain 2: Classroom Environment', 'Domain 3: Instruction', 'Domain 4: Professional Responsibilities'][i]"
                        :groups="Object.keys(item)"
                        :group-members="[2018, 2019, 2020]"
                        :data="item" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
    .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 1em;
    }
    .dashboard {
        width: 100%;
    }
    .content {
        padding-left: 2em;
        .description {
            font-size: 1em;
            line-height: 1.5;
            color: #6b6b6b;
        }
    }
    .components {
        border: 1px solid #2c557d;
        th {
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        th * {
            pointer-events: none;
        }
        th div {
            padding-top: 5px;
            margin-left: 1em;
        }
    }
    .header h2 {
        font-size: 2.25em;
        margin: 0 0 10px 0;
    }
    .header {
        .subtitle {
            font-size: 1.5em;
            margin: 0 0 10px 0;
        }
    }
    table {
        width: 100%;
        position: relative;
        z-index: 1;
        border-collapse: collapse;
    }
    .welcome-note {
        font-family: Arial;
        color: #333333;
        margin-bottom: 3em;
        padding: 1em;
        border: 2px solid #c4c4c4;
        border-radius: 8px;
        p {
          font-family: Arial;  
        }
        > p {
            font-size: 1.25em;
        }
        ul {
            padding: 0;
        }
        li {
            display: flex;
            align-items: center;
            p {
                font-size: 1.125em;
                font-weight: 400;
                margin: 0 0 0 20px;
            }
        }
        li:first-child {
            margin-bottom: 1em;
        }
    }
    .empty-state {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        font-size: .9375em;
        p {
            margin: 0;
            line-height: 1.5;
            font-family: Arial;
        }
        .empty-state-content {
            display: flex;
            align-items: flex-start;
            div {
                margin-left: 10px;
            }
        }
        @media all and (min-width: 600px) {
            height: 600px;
        }
    }
    .table-notes {
        margin-top: 1em;
        p {
            margin: 0;
            font-size: 15px;
            font-family: Arial;
        }
    }
    caption {
        text-align: left;
        padding: 1em 0;
        font-size: 1.875em;
    } 
    .dash-filters {
        margin: 2em 0 0 0;
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        @media all and (min-width: 950px) {
            flex-direction: row;
        }

        .toggle {
            width: 100%;
            margin-bottom: 2em;
            label {
                margin-bottom: 1em;
                color: #333;
                @media all and (min-width: 950px) {
                    margin-bottom: 0px;
                }
            }
            div {
                border: 1px solid #979797;
                display: flex;
                @media all and (min-width: 750px) {
                    flex-direction: column;
                }
            }
            button {
                border-radius: 0px;
            }
            @media all and (min-width: 750px) {
                width: 250px;
            }
            @media all and (min-width: 1024px) {
                width: 15%;
                margin-bottom: 0;
            }
        }
        .toggle button {
            width: 100%;
            padding: 5px 10px;
            border: none;
            text-align: center;
            cursor: pointer;
            color: #6d6565;
        }
        .toggle .average {
           background-color: #ffffff;
        }
        .toggle .count {
            background-color: #ffffff;
        }
        .toggle button.active {
            background-color: #2c557d;
            color: #ffffff;
        }
        .district {
            p {
                margin: 0 0 5px 0;
                font-size: 1em;
                color: #000000;
            }
            p.value {
                padding: 5px;
                font-size: 1em;
                border: 1px solid #979797;
                border-radius: 3px;
            }
        }
    }
    .filter-group {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        width: 100%;
        @media all and (min-width: 950px) {
            margin-left: 2em;
        }
        @media all and (min-width: 1024px) {
            padding: 0 1em;
            width: 75%;
        }
    }
    .hedi-scale {
        display: flex;
        margin: 2em 0;
    }
    .clear-filters-btn {
        display: flex;
        align-items: center;
        svg {
            margin-right: 10px;
            line {
                stroke: #000 !important;
            }
        }
    }
    .see-more-btn {
        width: 130px !important;
        border: none;
        text-decoration: underline;
        cursor: pointer;
        outline: none;
    }
    .filter-options {
        display: flex;
        div {
            margin-right: 1em;
        }
    }
    .filter {
        &:not(:last-child) {
            margin-bottom: 1em;
        }
        label {
            display: block;
        }
        select {
            width: 200px;
            padding: 5px;
            font-size: 1em;
            border: 1px solid #979797;
            border-radius: 3px;
        }
        button {
            font-size: 1em;
            width: 210px;
            background: transparent;
            border: 1px solid #979797;
            border-radius: 3px;
            padding: 5px 10px;
            text-align: left;
            outline: none;
        }
        .dropdown {
            position: relative;
            z-index: 10;
            ul {
                position: absolute;
                width: 200px;
                max-height: 200px;
                overflow: scroll;
                overflow-x: hidden;
                list-style-type: none;
                padding: 5px 20px;
                margin: 5px 0 0 0;
                border: 1px solid #979797;
                background-color: #fff;
                border-radius: 3px;
            }
            li {
                display: flex;
                align-items: center;
                margin-bottom: 5px;
                font-size: .875em;
            }
            li label {
                margin: 0;
            }
        }
    }
    .filter-actions {
        button {
            width: 115px;
            padding: 5px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            background-color: transparent;
            border: none;
            box-sizing: border-box;
            cursor: pointer;
            font-size: 1em;
            span {
                font-weight: bold;
            }
        }
    }
    .selections {
        width: 100%;
        ul {
            display: flex;
            padding: 0;
            list-style-type: none;
            li {
                padding: 5px;
                box-sizing: border-box;
                flex: 1;
                font-size: .8em;
                display: flex;
                justify-content: space-between;
                border: 1px solid #979797;
            }
            li button {
                background-color: transparent;
                border: none;
                cursor: pointer;
                font-size: .75em;
            }
            li:not(:last-child) {
                margin-right: 5px;
            }
        }
    }
    * {
        font-family: Arial;
        box-sizing: border-box;
    }
    thead > tr th:last-child {
        cursor: pointer;
    }
    thead tr, tbody tr, tfoot tr {
        display: flex;
        th, td {
            justify-content: flex-start;
        }
        th:first-child {
            flex-basis: 300px;
            justify-content: flex-start;
        }
        th:last-child {
            flex: 1;
        }
    }
    tbody tr td:first-child {
        flex-basis: 300px;
    }
    tbody tr td:last-child {
        flex: 1;
    }
    tbody {
        tr td {
            display: flex;
            align-items: center;
            cursor: pointer;
            span {
                display: block;
                height: 35px;
            }
        }
        td:not(:first-child) {
            justify-content: center;
        }
        td:last-child {
            border-right: none;
        }
    }
    tfoot {
        td:last-child {
            border: none;
        }
    }
    thead th {
        padding: 5px;
        font-weight: normal;
    }
    thead th.col {
        flex-basis: 85px;
    }
    thead tr:first-child {
        color: #ffffff;
        background-color: #2c557d;
        line-height: 1.5;
    }
    thead tr:nth-child(2) {
        th {
            background-color: #f0f0f0;
            border-color: #979797;
            position: relative;
            cursor: pointer;
            div {
                position: absolute;
                right: 5px;
                top: 10px;
            }
            div span {
                font-size: 12px;
                display: inline-block;
            }
        }
    }
    thead tr:first-child th:first-child {
        text-align: left;
    }
    thead th {
        text-align: center;
    }
    thead tr td:last-child {
        flex-basis: 150px;
    }
    tbody, thead, tfoot {
        display: block;
    }
    tbody td:not(:first-child), tfoot td:not(:first-child) {
        text-align: center;
    }
    tbody td, tfoot td {
        box-sizing: border-box;
        padding: 5px;
        line-height: 1.5;
        border: 1px solid #979797;
    }
    tbody tr, tfoot tr {
        span {
            display: inline-block;
            box-sizing: border-box;
            padding: 5px 15px;
        }
        td.component-data {
            flex-basis: 85px;
            color: #ffffff;
            .freq, .no-data {
                color: #000;
            }
        }
        td:last-child {
            flex: 1;
        }
    }
    tbody {
        height: 475px;
        overflow-y: auto;
        overflow-x: hidden;
    }
    tfoot {
        tr td:first-child {
            flex-basis: 300px;
            padding: 1.5em 5px;
        }
        tr td:first-child {
            border: 1px solid #949494;
            background-color: #f0f0f0;
        }
        tr td {
            display: flex;
            align-items: center;
            span {
                height: 35px;
            }
        }
        tr td:not(:first-child) {
            justify-content: center;
        }
    }
    .district-selections {
        width: 500px;
    }
    #district-visualizations {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: space-between;
    }
    .district-visualizations {
        margin-top: 3em;
        p {
            font-size: 1.5em;
            margin: 0 0 1em 0;
        }
        .content {
            padding-left: 2em;
            div p {
                font-size: 1em;
                line-height: 1.5;
                color: #6b6b6b;
            }
        }
    }
    .district-visualizations-legend {
        ul {
            display: flex;
            list-style-type: none;
            padding: 0;
        }
        ul li {
            width: 250px;
            display: flex;
            span {
                margin-right: 1em;
                display: block;
                height: 17px;
                width: 17px;
            }
        }
        li:first-child {
            span {
                background-color: #2c557d;
            }
        }
        li:nth-child(2) {
            span {
                background-color: #567797;
            }
        }
        li:last-child {
            span {
                background-color: #abbbcb;
            }
        }
    }
    .select {
        width: 300px;
    }
    .selected-types, .selected-districts {
        button {
            border: none;
            span {
                margin-right: 10px;
            }
        }
        ul {
           padding: 0;
           list-style: none;
           display: flex;
           flex-wrap: wrap; 
        }
        li {
            margin-bottom: 10px;
            border: 1px solid #979797;
            color: #979797;
            padding: 5px 10px 5px 5px;
        }
        li:not(:last-child) {
            margin-right: 10px;
        }
        svg {
            cursor: pointer;
            line {
                stroke: #979797;
            }
        }
    }
    .selected-districts {
        li {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 60px;
        }
    }
    .selected-types {
        li {
            width: 200px;
        }
    }
    .applied-filters {
        margin-top: 3em;
    }
    .active {
        fill: #949494;
    }
    .fade-enter-active, .fade-leave-active {
        transition: opacity .5s;
    }
    .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
        opacity: 0;
    }
    .dropdown-btn {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    .down-chevron {
        width: 12px;
        height: 12px;
    }
    .no-districts {
        display: flex;
        justify-content: center;
        align-items: center;
        @media all and (min-width: 650px) {
            height: 450px;
        }
    }
     tbody tr td.component-data .nofreq {
        color: red;
    }
</style>